/**
 * EvalRunPage (`/evals/groups/:slug/tasks/:taskSlug/runs/:runId`) — one run.
 *
 * Provenance block (model / datasets / variant / grading / source /
 * timing / error), metrics rendered verbatim from the pushed JSONB
 * (headline overall_score highlighted), and per-case results when
 * provided.
 */

import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'

import { TranscriptEvents } from '@/components/review/TrajectoryTranscript'
import { EvalBreadcrumb } from '@/components/evals/EvalBreadcrumb'
import { trajectoryToEvents } from '@/components/evals/trajectoryEvents'
import { ResultsMatrix } from '@/components/evals/ResultsMatrix'
import { RunStatusBadge } from '@/components/evals/RunStatusBadge'
import { KeyValueTable } from '@/components/evals/KeyValueTable'
import { formatDuration, formatScore, formatVariant } from '@/components/evals/format'
import { taskVariantPath } from '@/components/evals/taskVariant'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CopyButton } from '@/components/ui/copy-button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useEvalCaseRollout, useEvalRun } from '@/hooks/useEvals'
import { EvalRunDetailResponseStatus } from '@/api/generated/model'

function formatTimestamp(iso: string | null): string {
  if (iso === null) return '—'
  return new Date(iso).toLocaleString()
}

/** A path-like value (dataset, output location) in monospace with a copy button; em-dash when empty. */
function CopyablePath({ path }: { path: string }): React.JSX.Element {
  if (!path) return <>{'—'}</>
  return (
    <span className="inline-flex items-center gap-1 break-all font-mono text-xs">
      {path}
      <CopyButton value={path} />
    </span>
  )
}

function ProvenanceItem({
  label,
  value,
  className,
}: {
  label: string
  value: React.ReactNode
  className?: string
}): React.JSX.Element {
  return (
    <div className={className ?? 'space-y-0.5'}>
      <dt className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  )
}

/**
 * The `?trace=` view: one case's full multi-turn trajectory, fetched
 * from the eval-rollouts bucket prefix and rendered with the review
 * page's compact transcript rows (collapsed one-line peeks, expand per
 * row). A 404 means no rollout was exported for the trace, which the
 * view states.
 */
function TraceView({ traceId }: { traceId: string }): React.JSX.Element {
  const { data, isLoading, isError, error } = useEvalCaseRollout(traceId)
  if (isLoading) return <Skeleton className="h-60 w-full" />
  if (isError && getApiErrorStatus(error) === 404) {
    return (
      <p className="text-sm text-muted-foreground">
        No stored rollout for this trace. Only runs pushed with a rollout bank carry one.
      </p>
    )
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        Failed to load rollout: {getApiErrorDetail(error) ?? 'unknown error'}
      </p>
    )
  }
  return <TranscriptEvents events={trajectoryToEvents(data.messages, traceId)} />
}

export default function EvalRunPage(): React.JSX.Element {
  const { slug, taskSlug, runId } = useParams<{ slug: string; taskSlug: string; runId: string }>()
  const [searchParams] = useSearchParams()
  const trace = searchParams.get('trace')
  const { data: run, isLoading, isError, error } = useEvalRun(slug, taskSlug, runId)

  return (
    <PageContainer className="space-y-6">
      <EvalBreadcrumb
        crumbs={[
          { label: 'Eval Groups', to: '/evals' },
          {
            label: run?.group_display_name ?? slug ?? '',
            to: slug ? `/evals/groups/${slug}` : undefined,
          },
          {
            label: run ? `${run.task_display_name} · ${formatVariant(run.variant)}` : (taskSlug ?? ''),
            to:
              slug && taskSlug
                ? run
                  ? taskVariantPath({
                      groupSlug: slug,
                      taskSlug,
                      variant: run.variant,
                    })
                  : `/evals/groups/${slug}/tasks/${taskSlug}`
                : undefined,
          },
          { label: run?.model_slug ?? 'Run' },
        ]}
      />

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {isError && slug !== undefined && taskSlug !== undefined && getApiErrorStatus(error) === 404 && (
        <Navigate to={`/evals/groups/${slug}/tasks/${taskSlug}`} replace />
      )}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load run: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {/* ?trace= shows one case's full trajectory as its own view — linked
          from the per-case table, URL-addressable, back-button friendly. */}
      {!isLoading && !isError && run && trace !== null && (
        <>
          <PageHeader
            title={`${run.model_slug} · rollout`}
            description={`Trace ${trace}`}
            actions={
              <Button asChild variant="outline">
                <Link to={{ search: '' }}>Back to run</Link>
              </Button>
            }
          />
          <TraceView traceId={trace} />
        </>
      )}

      {!isLoading && !isError && run && trace === null && (
        <>
          <PageHeader
            title={`${run.model_slug} · ${formatScore(run.overall_score)}`}
            actions={<RunStatusBadge status={run.status} />}
          />

          {run.status === EvalRunDetailResponseStatus.failed && run.error && (
            <Alert variant="destructive">
              <AlertTitle>Run failed</AlertTitle>
              <AlertDescription className="whitespace-pre-wrap font-mono text-xs">
                {run.error}
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Provenance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* List layout: short facts share rows; long dataset paths get
                  their own full-width rows so they don't crush the columns. */}
              <dl className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <ProvenanceItem
                    label="Model"
                    value={<span className="font-mono text-xs">{run.model_slug}</span>}
                  />
                  <ProvenanceItem label="Variant" value={run.variant || '—'} />
                  <ProvenanceItem label="Grading" value={run.grading || '—'} />
                  <ProvenanceItem label="Metric" value={run.metric || '—'} />
                  <ProvenanceItem
                    label="Source pipeline"
                    value={run.source ? <span className="font-mono text-xs">{run.source}</span> : '—'}
                  />
                  <ProvenanceItem label="Started" value={formatTimestamp(run.started_at)} />
                  <ProvenanceItem label="Finished" value={formatTimestamp(run.finished_at)} />
                  <ProvenanceItem
                    label="Duration"
                    value={formatDuration(run.started_at, run.finished_at)}
                  />
                </div>
                <ProvenanceItem
                  label="Train dataset"
                  value={<CopyablePath path={run.dataset_train} />}
                  className="space-y-0.5 border-t border-border/50 pt-4"
                />
                <ProvenanceItem
                  label="Eval dataset"
                  value={<CopyablePath path={run.dataset_eval} />}
                />
              </dl>
            </CardContent>
          </Card>

          {(run.input_template || run.output_location) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Inputs & outputs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {run.input_template && (
                  <div className="space-y-0.5">
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Input template
                    </p>
                    <p className="whitespace-pre-wrap font-mono text-xs text-foreground">
                      {run.input_template}
                    </p>
                  </div>
                )}
                {run.output_location && (
                  <div className="space-y-0.5">
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Output location
                    </p>
                    <p className="text-foreground">
                      <CopyablePath path={run.output_location} />
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <KeyValueTable
            title="Metrics"
            data={run.metrics}
            highlight={{ key: 'overall_score', value: formatScore(run.overall_score) }}
          />

          {run.cases.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground">Per-case results</h2>
              <ResultsMatrix cases={run.cases} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  )
}
